import React, { useState } from 'react';
import particularesApiService from '../services/ParticularesApiService';
import ParticularModal from './common/ParticularModal';

// Horario semanal de un curso (días y franjas) y período de cursada opcional.
// Los alumnos matriculados lo ven en su calendario.

export const DIAS_SEMANA = [
  { value: 1, label: 'Lunes', short: 'Lun' },
  { value: 2, label: 'Martes', short: 'Mar' },
  { value: 3, label: 'Miércoles', short: 'Mié' },
  { value: 4, label: 'Jueves', short: 'Jue' },
  { value: 5, label: 'Viernes', short: 'Vie' },
  { value: 6, label: 'Sábado', short: 'Sáb' },
  { value: 0, label: 'Domingo', short: 'Dom' }
];

const getValue = (source, lower, upper) => source?.[lower] ?? source?.[upper];

export const getCursoHorarios = (curso) => {
  const horarios = getValue(curso, 'horarios', 'Horarios');
  return Array.isArray(horarios)
    ? horarios.map((h) => ({
      diaSemana: Number(getValue(h, 'diaSemana', 'DiaSemana')),
      horaInicio: getValue(h, 'horaInicio', 'HoraInicio') || '',
      horaFin: getValue(h, 'horaFin', 'HoraFin') || ''
    }))
    : [];
};

// "Lun 18:00–19:30 · Mié 18:00–19:30"
export const formatHorariosResumen = (horarios) => horarios
  .slice()
  .sort((a, b) => ((a.diaSemana + 6) % 7) - ((b.diaSemana + 6) % 7) || a.horaInicio.localeCompare(b.horaInicio))
  .map((h) => `${DIAS_SEMANA.find((d) => d.value === h.diaSemana)?.short || ''} ${h.horaInicio}–${h.horaFin}`)
  .join(' · ');

const newRow = (diaSemana = 1) => ({ key: `${Date.now()}-${Math.random()}`, diaSemana, horaInicio: '18:00', horaFin: '19:00' });

const ParticularCursoHorarioModal = ({ curso, cursoNombre, onClose, onSaved }) => {
  const cursoId = curso?.id || curso?.Id;
  const [rows, setRows] = useState(() => getCursoHorarios(curso).map((h) => ({ ...newRow(), ...h })));
  const [fechaInicio, setFechaInicio] = useState(getValue(curso, 'fechaInicioCursada', 'FechaInicioCursada') || '');
  const [fechaFin, setFechaFin] = useState(getValue(curso, 'fechaFinCursada', 'FechaFinCursada') || '');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const updateRow = (key, patch) => setRows((prev) => prev.map((row) => (row.key === key ? { ...row, ...patch } : row)));
  const removeRow = (key) => setRows((prev) => prev.filter((row) => row.key !== key));
  const addRow = () => setRows((prev) => {
    const last = prev[prev.length - 1];
    return [...prev, last ? { ...newRow(), diaSemana: last.diaSemana, horaInicio: last.horaInicio, horaFin: last.horaFin } : newRow()];
  });

  const handleSubmit = async (event) => {
    event.preventDefault();
    const invalid = rows.find((row) => !row.horaInicio || !row.horaFin || row.horaFin <= row.horaInicio);
    if (invalid) {
      setError('En cada franja, la hora de fin tiene que ser posterior a la de inicio.');
      return;
    }
    if (fechaInicio && fechaFin && fechaFin < fechaInicio) {
      setError('La fecha de fin de cursada no puede ser anterior a la de inicio.');
      return;
    }

    setSaving(true);
    setError('');
    try {
      await particularesApiService.setCursoHorarios(cursoId, {
        fechaInicioCursada: fechaInicio || null,
        fechaFinCursada: fechaFin || null,
        horarios: rows.map((row) => ({ diaSemana: Number(row.diaSemana), horaInicio: row.horaInicio, horaFin: row.horaFin }))
      });
      onSaved?.();
      onClose();
    } catch (err) {
      setError(err.message || 'No pudimos guardar el horario del curso.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <ParticularModal title="Horario del curso" icon="fas fa-clock" onClose={saving ? () => {} : onClose} className="particular-modal-wide">
      <form className="particular-modal-body" onSubmit={handleSubmit}>
        <p className="particular-matricula-intro">
          Definí en qué días y horarios se dicta <strong>{cursoNombre}</strong>. Los alumnos matriculados lo verán en su calendario.
        </p>

        <div className="particular-horario-rows">
          {rows.length === 0 && (
            <p className="particular-matricula-muted">Sin horario cargado. Agregá al menos una franja.</p>
          )}
          {rows.map((row) => (
            <div className="particular-horario-row" key={row.key}>
              <label>
                Día
                <select value={row.diaSemana} onChange={(event) => updateRow(row.key, { diaSemana: Number(event.target.value) })} disabled={saving}>
                  {DIAS_SEMANA.map((dia) => <option key={dia.value} value={dia.value}>{dia.label}</option>)}
                </select>
              </label>
              <label>
                Desde
                <input type="time" step="300" value={row.horaInicio} onChange={(event) => updateRow(row.key, { horaInicio: event.target.value })} disabled={saving} required />
              </label>
              <label>
                Hasta
                <input type="time" step="300" value={row.horaFin} onChange={(event) => updateRow(row.key, { horaFin: event.target.value })} disabled={saving} required />
              </label>
              <button type="button" className="particular-icon-btn danger" onClick={() => removeRow(row.key)} disabled={saving} title="Quitar franja">
                <i className="fas fa-trash" />
              </button>
            </div>
          ))}
          <button type="button" className="particular-secondary-btn particular-horario-add" onClick={addRow} disabled={saving}>
            <i className="fas fa-plus" />
            Agregar franja
          </button>
        </div>

        <div className="particular-horario-periodo">
          <span>Período de cursada (opcional)</span>
          <div>
            <label>
              Inicio
              <input type="date" value={fechaInicio} onChange={(event) => setFechaInicio(event.target.value)} disabled={saving} />
            </label>
            <label>
              Fin
              <input type="date" value={fechaFin} onChange={(event) => setFechaFin(event.target.value)} disabled={saving} />
            </label>
          </div>
          <small>Si lo dejás vacío, el horario se repite todas las semanas sin fecha de fin.</small>
        </div>

        {error && <div className="particular-matricula-error">{error}</div>}

        <div className="particular-modal-footer">
          <button type="button" className="particular-secondary-btn" onClick={onClose} disabled={saving}>Cancelar</button>
          <button type="submit" className="particular-primary-btn" disabled={saving}>
            <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`} />
            Guardar horario
          </button>
        </div>
      </form>
    </ParticularModal>
  );
};

export default ParticularCursoHorarioModal;
